import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import type { ResponseArray, ResponseObject } from '@/types/shared';

/**
 * Contract Negotiations — global standalone list (`GET /contracts/negotiations`).
 * Shapes verified against live staging: list item[0] keys + detail keys probed 2026-08-27.
 * Histories are 403/500 unless the caller is an assignee — the history tab handles
 * that error gracefully (shows empty state).
 */

/* ── Types ──────────────────────────────────────────────────────────────── */

export type NegotiationStatus =
  | 'draft'
  | 'in_progress'
  | 'pending_review'
  | 'waiting_approval'
  | 'completed'
  | 'rejected'
  | 'cancelled'
  | 'archived'
  | 'converted';

export type NegotiationPriority = 'low' | 'medium' | 'high' | 'critical';

export type NegotiationType =
  | 'quote_request'
  | 'contract_amendment'
  | 'price_negotiation'
  | 'renewal'
  | 'termination';

export type NegotiationStage =
  | 'initial_contact'
  | 'needs_assessment'
  | 'proposal'
  | 'negotiation'
  | 'contract_preparation'
  | 'review'
  | 'approval'
  | 'closed';

/** `GET /contracts/negotiations` list row — probed 2026-08-27. */
export interface ContractNegotiationRow {
  contract_negotiation_id: number;
  contract_negotiation_code: string;
  contract_negotiation_title: string;
  contract_negotiation_type: string;
  contract_negotiation_content: string | null;
  contract_negotiation_status: string;
  contract_negotiation_priority: string;
  contract_negotiation_deadline: string | null;
  contract_negotiation_employee_id: number | null;
  contract_negotiation_assignees: number[];
  contract_negotiation_customer_id: number | null;
  contract_negotiation_stage: string | null;
  contract_negotiation_notes: string | null;
  contract_negotiation_value: number;
  contract_negotiation_progress: number | null;
  contract_negotiation_customer_journey_id: number | null;
  contract_negotiation_customer_journey_step: string | null;
  archived: boolean;
  archived_at: string | null;
  archived_by: number | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  deleted_by: number | null;
  customer_name: string | null;
  company_name: string | null;
  creator_name: string | null;
  assignee_names: string[];
}

/** `GET /contracts/negotiations/:id` — detail record (adds attachments, drops company_name). */
export interface ContractNegotiationDetail extends ContractNegotiationRow {
  attachments: Array<{
    attachment_id?: number;
    attachment_file_name?: string;
    attachment_file_url?: string;
    attachment_file_size?: number | null;
    attachment_file_note?: string | null;
    created_at?: string;
    [key: string]: unknown;
  }>;
}

/** `GET /contracts/negotiations/:id/histories` row. */
export interface ContractNegotiationHistory {
  contract_negotiation_history_id?: number;
  contract_negotiation_id?: number;
  history_status?: string;
  history_note?: string | null;
  history_value?: number | null;
  employee_name?: string | null;
  created_at?: string;
  [key: string]: unknown;
}

/* ── Params ─────────────────────────────────────────────────────────────── */

export interface NegotiationListParams {
  page?: number;
  page_size?: number;
  q?: string;
  status?: string;
  priority?: string;
  type?: string;
  stage?: string;
  archived?: boolean;
}

/* ── Query keys ─────────────────────────────────────────────────────────── */

export const negotiationKeys = {
  all: ['negotiations'] as const,
  list: (params: NegotiationListParams) => ['negotiations', 'list', params] as const,
  detail: (id: number) => ['negotiations', 'detail', id] as const,
  histories: (id: number) => ['negotiations', id, 'histories'] as const,
};

/* ── Helpers ─────────────────────────────────────────────────────────────── */

function buildListQuery(params: NegotiationListParams): string {
  const out: Record<string, string | number | boolean> = {};
  if (params.page !== undefined) out.page = params.page;
  if (params.page_size !== undefined) out.page_size = params.page_size;
  if (params.q) out.q = params.q;
  if (params.status) out.status = params.status;
  if (params.priority) out.priority = params.priority;
  if (params.type) out.type = params.type;
  if (params.stage) out.stage = params.stage;
  if (params.archived !== undefined) out.archived = params.archived;
  return qs.stringify(out);
}

/* ── Queries ─────────────────────────────────────────────────────────────── */

/** `GET /contracts/negotiations` — paginated negotiation list. */
export function useContractNegotiations(params: NegotiationListParams = {}) {
  return useQuery({
    queryKey: negotiationKeys.list(params),
    queryFn: async (): Promise<ResponseArray<ContractNegotiationRow>> => {
      const res = await dataCentralApi.get<ResponseArray<ContractNegotiationRow>>(
        `/contracts/negotiations?${buildListQuery({ page: 1, page_size: 20, ...params })}`,
      );
      return res.data;
    },
  });
}

/** `GET /contracts/negotiations/:id` — single negotiation record. */
export function useContractNegotiationDetail(id: number) {
  return useQuery({
    queryKey: negotiationKeys.detail(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseObject<ContractNegotiationDetail>> => {
      const res = await dataCentralApi.get<ResponseObject<ContractNegotiationDetail>>(
        `/contracts/negotiations/${id}`,
      );
      return res.data;
    },
  });
}

/* ── Mutation types ─────────────────────────────────────────────────────── */

/** Payload for `POST /contracts/negotiations/:id/reject` and `/:id/cancel`. */
export interface NegotiationRejectPayload {
  /** Required by backend (`binding:"required"`). */
  reason: string;
  notes?: string;
}

/** Payload for `POST /contracts/negotiations/:id/resolve`. */
export interface NegotiationResolvePayload {
  /** Required by backend (`binding:"required"`). */
  resolution_notes: string;
}

/* ── Mutation hooks ─────────────────────────────────────────────────────── */

/** `POST /contracts/negotiations/:id/reject` — reject a negotiation. */
export function useRejectNegotiation(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: NegotiationRejectPayload): Promise<ResponseObject<unknown>> => {
      const res = await dataCentralApi.post<ResponseObject<unknown>>(
        `/contracts/negotiations/${id}/reject`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: negotiationKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: negotiationKeys.all });
    },
  });
}

/** `POST /contracts/negotiations/:id/resolve` — resolve (complete) a negotiation. */
export function useResolveNegotiation(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: NegotiationResolvePayload): Promise<ResponseObject<unknown>> => {
      const res = await dataCentralApi.post<ResponseObject<unknown>>(
        `/contracts/negotiations/${id}/resolve`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: negotiationKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: negotiationKeys.all });
    },
  });
}

/** `POST /contracts/negotiations/:id/cancel` — cancel a negotiation (requires reason). */
export function useCancelNegotiation(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: NegotiationRejectPayload): Promise<ResponseObject<unknown>> => {
      const res = await dataCentralApi.post<ResponseObject<unknown>>(
        `/contracts/negotiations/${id}/cancel`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: negotiationKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: negotiationKeys.all });
    },
  });
}

/**
 * `POST /contracts/negotiations/:id/archive` — archive a negotiation.
 * Backend does NOT require a request body (no binding on that handler).
 */
export function useArchiveNegotiation(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<ResponseObject<unknown>> => {
      const res = await dataCentralApi.post<ResponseObject<unknown>>(
        `/contracts/negotiations/${id}/archive`,
        {},
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: negotiationKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: negotiationKeys.all });
    },
  });
}

/* ── Queries ─────────────────────────────────────────────────────────────── */

/**
 * `GET /contracts/negotiations/:id/histories` — history log.
 * Returns 403/500 unless the caller is the creator or an assignee.
 * The caller must handle errors gracefully.
 */
export function useContractNegotiationHistories(id: number) {
  return useQuery({
    queryKey: negotiationKeys.histories(id),
    enabled: !!id,
    retry: false,
    queryFn: async (): Promise<ResponseArray<ContractNegotiationHistory>> => {
      const res = await dataCentralApi.get<ResponseArray<ContractNegotiationHistory>>(
        `/contracts/negotiations/${id}/histories`,
      );
      return res.data;
    },
  });
}
